import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, BookOpen, PlayCircle, X } from "lucide-react";
import { api } from "@/lib/api";

// Small contextual "Need help?" popover — deep-links feature pages to the Help Center.
export const ContextualHelp = ({ featureKey, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open || data) return;
    api.get(`/help/contextual/${featureKey}`).then((r) => setData(r.data)).catch(() => setData({ articles: [], videos: [] }));
  }, [open, data, featureKey]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`contextual-help-btn-${featureKey}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg-surface px-3 py-1 text-[11px] text-ink-secondary hover:text-brand-amber hover:border-brand-amber/40 transition">
        <HelpCircle className="h-3 w-3" /> Need help?
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 rounded-xl border border-line bg-bg-elevated shadow-2xl p-4"
          data-testid={`contextual-help-panel-${featureKey}`}>
          <div className="flex items-center justify-between mb-2.5">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-amber">// Help for this page</div>
            <button type="button" onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-primary">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {!data && <div className="text-xs text-ink-muted py-3">Loading…</div>}
          {data && (
            <div className="space-y-1.5">
              {(data.articles || []).map((a) => (
                <Link key={a.slug} to={`/dashboard/help/article/${a.slug}`} onClick={() => setOpen(false)}
                  data-testid={`contextual-article-${a.slug}`}
                  className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-bg-surface transition">
                  <BookOpen className="h-3.5 w-3.5 text-brand-amber mt-0.5 shrink-0" />
                  <span className="text-xs text-ink-secondary leading-snug">{a.title}</span>
                </Link>
              ))}
              {(data.videos || []).map((v) => (
                <Link key={v.video_id} to="/dashboard/help/videos" onClick={() => setOpen(false)}
                  className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-bg-surface transition">
                  <PlayCircle className="h-3.5 w-3.5 text-sky-400 mt-0.5 shrink-0" />
                  <span className="text-xs text-ink-secondary leading-snug">{v.title} <span className="text-ink-muted">(video)</span></span>
                </Link>
              ))}
              {data.articles?.length === 0 && data.videos?.length === 0 && (
                <div className="text-xs text-ink-muted py-2">No matching guides yet.</div>
              )}
              <div className="pt-2 mt-1 border-t border-line flex items-center justify-between">
                <Link to="/dashboard/help" onClick={() => setOpen(false)} className="text-[11px] text-brand-amber hover:underline">Open Help Center →</Link>
                <Link to="/dashboard/support/new" onClick={() => setOpen(false)} className="text-[11px] text-ink-muted hover:text-ink-primary">Contact support</Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
